import { useRef } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import ReportExportButtons from "../../components/Common/Report/ReportExportButtons";
import useCompanyBranding from "../../components/Common/Report/useCompanyBranding";
import { convertDoubleToStringPTBR } from "../../../utils/ConverterUtils";
import { formatDate } from "../../../utils/DateUtils";
import './EmployeeFinancialSummary.css';

const formatCurrency = (value) => convertDoubleToStringPTBR(Number(value || 0));
const formatQuantity = (value) => {
    const amount = Number(value || 0);
    return Number.isInteger(amount) ? String(amount) : amount.toFixed(2).replace('.', ',');
};

/** Rótulo da forma de pagamento; o backend manda o nome do enum. */
const PAYMENT_LABELS = {
    MONEY: 'Dinheiro',
    PIX: 'PIX',
    CREDIT_CARD: 'Cartão de crédito',
    DEBIT_CARD: 'Cartão de débito',
    BANK_TRANSFER: 'Transferência',
    DEPOSIT: 'Depósito',
    SUBSCRIPTION: 'Assinatura',
    NOT_SELECTED: '-',
};

/**
 * Estilos que acompanham o documento na janela de impressão. Ela não herda o CSS da
 * aplicação, então cabeçalho, logo, resumo lado a lado e assinatura precisam ir juntos,
 * senão saem do PDF (era o caso da linha de assinatura, que sumia).
 */
const PRINT_STYLES = `
    /* Comprovante de acerto: retrato, como o papel que vai para a pasta assinado. */
    @page { size: A4 portrait; margin: 14mm 12mm; }
    /* O Chrome salva PDF com "gráficos de segundo plano" desmarcado por padrão, o que
       apaga as faixas dos títulos e o cabeçalho das tabelas e faz o documento sair sem
       formatação. print-color-adjust manda imprimir os fundos assim mesmo. */
    html, body, * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    body { font-size: 12px; }
    .financial-summary__header { margin-bottom: 20px; padding-bottom: 12px; border-bottom: 2px solid #d32f2f; text-align: center; }
    .financial-summary__logo { display: block; max-width: 170px; max-height: 80px; margin: 0 auto 10px; object-fit: contain; }
    .financial-summary__header h2 { margin: 0 0 6px; font-size: 16px; }
    .financial-summary__header h1 { margin: 0 0 6px; font-size: 19px; letter-spacing: 0.08em; }
    .financial-summary__header strong { display: block; font-size: 13px; }
    .financial-summary__header p { margin: 2px 0 0; color: #5f6368; font-size: 11px; }
    .financial-summary__block-title { margin: 20px 0 8px; padding: 7px 10px; background: #f3f4f6; border-left: 3px solid #d32f2f; font-size: 12px; letter-spacing: 0.04em; text-transform: uppercase; }
    .financial-summary__table-title { margin: 12px 0 4px; color: #5f6368; font-size: 11px; font-style: italic; }
    .financial-summary__table { margin-top: 4px; font-size: 10.5px; }
    .financial-summary__table th, .financial-summary__table td { padding: 5px 7px; }
    .financial-summary__table tfoot td { background: #f3f4f6; }
    /* Recebimentos e descontos lado a lado, como no papel. Flex porque a janela de
       impressão não carrega o grid da folha da aplicação. */
    .financial-summary__totals { display: flex; gap: 16px; align-items: flex-start; }
    .financial-summary__totals > table { flex: 1; width: auto; }
    .financial-summary__negative td { color: #b91c1c; }
    /* Espaço de assinatura: o bloco inteiro não pode quebrar entre a linha e o nome. */
    .financial-summary__signature { margin: 60px auto 0; width: 100%; text-align: center; page-break-inside: avoid; }
    /* !important: o estilo inline usa a cor do tema, e no papel o traço e o nome são sempre pretos. */
    .financial-summary__signature-line { display: block; width: 55%; height: 0; margin: 0 auto 8px; border-top: 1px solid #111 !important; }
    .financial-summary__signature p { margin: 0; font-size: 12px; font-weight: 700; color: #111 !important; }
    .financial-summary__signature small { display: block; margin-top: 3px; color: #6b7280 !important; font-size: 10px; }
`;

/**
 * Resumo financeiro do profissional: o documento de fechamento do período, com o que ele
 * recebe, o que é descontado e o líquido a pagar, no formato que a barbearia entrega
 * impresso e assinado.
 *
 * Diferente do extrato, que é a tela de conferência do gestor, este é o comprovante do
 * acerto: uma linha por atendimento, os vales e consumos do período e o total que sobra.
 *
 * O vale entra aqui como desconto porque é isso que ele é no acerto. No extrato ele segue
 * apenas informativo, sem alterar a comissão (ver docs/cash-flow/advance).
 */
const FinancialSummaryDocument = ({ detail, period, companyName, serviceItems = [], productItems = [] }) => {

    const { t } = useTranslation();
    const contentRef = useRef(null);
    const branding = useCompanyBranding();

    if (!detail) {
        return null;
    }

    const headerName = companyName || branding.name;
    const periodLabel = `${moment(period.startDate).format('DD/MM/YYYY')} a ${moment(period.endDate).format('DD/MM/YYYY')}`;
    const paymentLabel = (method) => PAYMENT_LABELS[method] || method || '-';

    const servicesTotal = serviceItems.reduce((total, item) => total + Number(item.commissionValue || 0), 0);
    const productsTotal = productItems.reduce((total, item) => total + Number(item.commissionValue || 0), 0);
    const advances = detail.advances || [];
    const advancesTotal = Number(detail.advancesTotal || 0);
    // Consumos pendentes do período (refrigerante, água), os mesmos que o extrato e o portal
    // descontam. Sem eles este papel, que é o que o colaborador assina, mostrava um líquido
    // maior do que o que de fato seria pago.
    const consumptions = detail.consumptions || [];
    const consumptionsTotal = Number(detail.consumptionsTotal || 0);
    const discountsTotal = advancesTotal + consumptionsTotal;
    const consumptionPriceLabel = (priceType) => priceType === 'COST' ? 'Preço de custo' : 'Preço normal';

    // Bonificações do período: valor combinado com a casa, que ele recebe como a comissão.
    // Entram nos recebimentos para o papel assinado fechar com o que é pago.
    const bonuses = detail.bonuses || [];
    const bonusesTotal = Number(detail.bonusesTotal || 0);

    const earningsTotal = servicesTotal + productsTotal + bonusesTotal;
    const netTotal = earningsTotal - discountsTotal;

    const exportRows = [
        ...serviceItems.map(item => [
            'Serviço',
            formatDate(item.serviceDate || item.createdAt),
            item.customerName || '-',
            item.product?.name || '-',
            formatCurrency(item.total),
            paymentLabel(item.paymentMethod),
            formatCurrency(item.commissionValue),
        ]),
        ...productItems.map(item => [
            'Produto',
            formatDate(item.serviceDate || item.createdAt),
            item.customerName || '-',
            item.product?.name || '-',
            formatCurrency(item.total),
            paymentLabel(item.paymentMethod),
            formatCurrency(item.commissionValue),
        ]),
        ...bonuses.map(bonus => [
            'Bonificação',
            '-',
            '-',
            `${bonus.description || 'Bonificação'} (${bonus.months}x ${formatCurrency(bonus.value)})`,
            '-',
            '-',
            formatCurrency(bonus.total),
        ]),
        ...advances.map(advance => [
            'Vale',
            formatDate(advance.advanceDate),
            '-',
            advance.description || 'Vale',
            '-',
            '-',
            `-${formatCurrency(advance.value)}`,
        ]),
        ...consumptions.map(consumption => [
            'Consumo',
            formatDate(consumption.consumptionDate),
            '-',
            `${formatQuantity(consumption.quantity)}x ${consumption.productName || '-'} (${consumptionPriceLabel(consumption.priceType)})`,
            '-',
            '-',
            `-${formatCurrency(consumption.total)}`,
        ]),
    ];

    return (
        <div className="financial-summary">
            <div className="financial-summary__actions d-print-none">
                <ReportExportButtons
                    contentRef={contentRef}
                    documentTitle={`resumo-financeiro-${detail.summary.employeeName}`}
                    fileName={`resumo-financeiro-${detail.summary.employeeName}`}
                    preambleRows={[
                        ['Resumo Financeiro'],
                        [t('employee'), detail.summary.employeeName],
                        ['Período de pagamento', periodLabel],
                    ]}
                    headers={['Tipo', 'Data', 'Cliente', 'Descrição', 'Valor R$', 'Forma de pagamento', 'Valor profissional R$']}
                    rows={exportRows}
                    extraStyles={PRINT_STYLES}
                />
            </div>

            <div ref={contentRef} className="financial-summary__document">
                <header className="financial-summary__header">
                    {branding.logo && (
                        <img
                            className="financial-summary__logo"
                            src={branding.logo}
                            alt={headerName || 'Logo da empresa'}
                            style={{ display: 'block', maxWidth: '170px', maxHeight: '80px', margin: '0 auto 10px', objectFit: 'contain' }}
                        />
                    )}
                    {headerName && <h2>{headerName}</h2>}
                    <h1>RESUMO FINANCEIRO</h1>
                    <strong>{detail.summary.employeeName}</strong>
                    <p>Período de pagamento: {periodLabel}</p>
                </header>

                <h3 className="financial-summary__block-title">Descritivo das receitas variáveis no período</h3>

                <h4 className="financial-summary__table-title">Sobre serviços</h4>
                <table className="financial-summary__table">
                    <thead>
                        <tr>
                            <th>Data do atendimento</th>
                            <th>Cliente</th>
                            <th>Serviço</th>
                            <th className="text-end">Valor R$</th>
                            <th>Forma de pagamento</th>
                            <th className="text-end">Valor profissional R$</th>
                        </tr>
                    </thead>
                    <tbody>
                        {serviceItems.length === 0 ? (
                            <tr><td colSpan={6} className="text-center">Nenhum serviço no período.</td></tr>
                        ) : serviceItems.map(item => (
                            <tr key={item.id}>
                                <td>{formatDate(item.serviceDate || item.createdAt)}</td>
                                <td>{item.customerName || '-'}</td>
                                <td>{item.product?.name || '-'}</td>
                                <td className="text-end">{formatCurrency(item.total)}</td>
                                <td>{paymentLabel(item.paymentMethod)}</td>
                                <td className="text-end">{formatCurrency(item.commissionValue)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={5} className="text-end"><b>Total</b></td>
                            <td className="text-end"><b>{formatCurrency(servicesTotal)}</b></td>
                        </tr>
                    </tfoot>
                </table>

                {productItems.length > 0 && (
                    <>
                        <h4 className="financial-summary__table-title">Sobre produtos vendidos</h4>
                        <table className="financial-summary__table">
                            <thead>
                                <tr>
                                    <th>Produto</th>
                                    <th className="text-end">Quantidade</th>
                                    <th className="text-end">Valor em produtos R$</th>
                                    <th className="text-end">Valor profissional R$</th>
                                    <th>Data da venda</th>
                                    <th>Cliente</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productItems.map(item => (
                                    <tr key={item.id}>
                                        <td>{item.product?.name || '-'}</td>
                                        <td className="text-end">{formatQuantity(item.qty)}</td>
                                        <td className="text-end">{formatCurrency(item.total)}</td>
                                        <td className="text-end">{formatCurrency(item.commissionValue)}</td>
                                        <td>{formatDate(item.serviceDate || item.createdAt)}</td>
                                        <td>{item.customerName || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className="text-end"><b>Total</b></td>
                                    <td className="text-end"><b>{formatQuantity(productItems.reduce((total, item) => total + Number(item.qty || 0), 0))}</b></td>
                                    <td className="text-end"><b>{formatCurrency(productItems.reduce((total, item) => total + Number(item.total || 0), 0))}</b></td>
                                    <td className="text-end"><b>{formatCurrency(productsTotal)}</b></td>
                                    <td colSpan={2}></td>
                                </tr>
                            </tfoot>
                        </table>
                    </>
                )}

                {/* Bonificações antes dos descontos, porque são recebimento. Valor por mês e
                    meses aparecem para o total não ser um número que ninguém reconhece. */}
                {bonuses.length > 0 && (
                    <>
                        <h3 className="financial-summary__block-title">Bonificações no período</h3>
                        <table className="financial-summary__table">
                            <thead>
                                <tr><th>Descrição</th><th className="text-end">Valor por mês R$</th><th className="text-end">Meses</th><th className="text-end">Total R$</th></tr>
                            </thead>
                            <tbody>
                                {bonuses.map(bonus => (
                                    <tr key={bonus.id}>
                                        <td>{bonus.description || 'Bonificação'}</td>
                                        <td className="text-end">{formatCurrency(bonus.value)}</td>
                                        <td className="text-end">{bonus.months}</td>
                                        <td className="text-end">{formatCurrency(bonus.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={3} className="text-end"><b>Total</b></td>
                                    <td className="text-end"><b>{formatCurrency(bonusesTotal)}</b></td>
                                </tr>
                            </tfoot>
                        </table>
                    </>
                )}

                {(advances.length > 0 || consumptions.length > 0) && (
                    <h3 className="financial-summary__block-title">Descritivo dos descontos variáveis no período</h3>
                )}

                {advances.length > 0 && (
                    <>
                        <h4 className="financial-summary__table-title">Vales / adiantamento</h4>
                        <table className="financial-summary__table">
                            <thead>
                                <tr><th>Data</th><th>Descrição</th><th className="text-end">Valor do vale R$</th></tr>
                            </thead>
                            <tbody>
                                {advances.map(advance => (
                                    <tr key={advance.id}>
                                        <td>{formatDate(advance.advanceDate)}</td>
                                        <td>{advance.description || 'Vale'}</td>
                                        <td className="text-end">{formatCurrency(advance.value)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={2} className="text-end"><b>Total</b></td>
                                    <td className="text-end"><b>{formatCurrency(advancesTotal)}</b></td>
                                </tr>
                            </tfoot>
                        </table>
                    </>
                )}

                {consumptions.length > 0 && (
                    <>
                        <h4 className="financial-summary__table-title">Consumos</h4>
                        <table className="financial-summary__table">
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Produto</th>
                                    <th className="text-end">Quantidade</th>
                                    <th>Preço aplicado</th>
                                    <th className="text-end">Valor R$</th>
                                </tr>
                            </thead>
                            <tbody>
                                {consumptions.map(consumption => (
                                    <tr key={consumption.id}>
                                        <td>{formatDate(consumption.consumptionDate)}</td>
                                        <td>{consumption.productName || '-'}</td>
                                        <td className="text-end">{formatQuantity(consumption.quantity)}</td>
                                        <td>{consumptionPriceLabel(consumption.priceType)} ({formatCurrency(consumption.unitPrice)})</td>
                                        <td className="text-end">{formatCurrency(consumption.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={4} className="text-end"><b>Total</b></td>
                                    <td className="text-end"><b>{formatCurrency(consumptionsTotal)}</b></td>
                                </tr>
                            </tfoot>
                        </table>
                    </>
                )}

                <h3 className="financial-summary__block-title">Resumo</h3>
                <div className="financial-summary__totals">
                    <table className="financial-summary__table">
                        <thead><tr><th colSpan={2}>Recebimentos</th></tr></thead>
                        <tbody>
                            <tr><td>Sobre serviços</td><td className="text-end">{formatCurrency(servicesTotal)}</td></tr>
                            <tr><td>Sobre produtos vendidos</td><td className="text-end">{formatCurrency(productsTotal)}</td></tr>
                            {bonusesTotal > 0 && (
                                <tr><td>Bonificações</td><td className="text-end">{formatCurrency(bonusesTotal)}</td></tr>
                            )}
                        </tbody>
                        <tfoot>
                            <tr><td><b>Total de recebimentos</b></td><td className="text-end"><b>{formatCurrency(earningsTotal)}</b></td></tr>
                        </tfoot>
                    </table>

                    <table className="financial-summary__table">
                        <thead><tr><th colSpan={2}>Descontos</th></tr></thead>
                        <tbody>
                            <tr><td>Vales / adiantamento</td><td className="text-end">{formatCurrency(advancesTotal)}</td></tr>
                            <tr><td>Consumos</td><td className="text-end">{formatCurrency(consumptionsTotal)}</td></tr>
                        </tbody>
                        <tfoot>
                            <tr><td><b>Total de descontos</b></td><td className="text-end"><b>{formatCurrency(discountsTotal)}</b></td></tr>
                            {/* O líquido pode ficar negativo quando vales e consumos passaram do
                                ganho do período: é uma informação do acerto, não um erro a esconder. */}
                            <tr className={netTotal < 0 ? 'financial-summary__negative' : ''}>
                                <td><b>Total</b></td>
                                <td className="text-end"><b>{formatCurrency(netTotal)}</b></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Campo de assinatura com estilo inline de propósito: o PDF é montado a
                    partir do innerHTML numa janela nova, e o que está inline vai junto,
                    sem depender de nenhuma folha de estilo chegar lá. As cores usam a variável
                    do tema com o preto como reserva: na tela escura o traço e o nome seguem a
                    cor do texto (antes sumiam no fundo), e na janela do PDF, onde a variável
                    não existe, vale o preto. */}
                <div
                    className="financial-summary__signature"
                    style={{ marginTop: '60px', width: '100%', textAlign: 'center', pageBreakInside: 'avoid' }}
                >
                    <div
                        className="financial-summary__signature-line"
                        style={{ width: '55%', margin: '0 auto 8px', borderTop: '1px solid var(--colmeia-ink, #111)', height: '1px' }}
                    ></div>
                    <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: 'var(--colmeia-ink, #111)' }}>
                        {detail.summary.employeeName}
                    </p>
                    <small style={{ display: 'block', marginTop: '3px', fontSize: '11px', color: 'var(--colmeia-muted, #6b7280)' }}>
                        Assinatura do profissional
                    </small>
                </div>
            </div>
        </div>
    );
};

export default FinancialSummaryDocument;
